import axios from 'axios';
import { documentAPI } from '../services/api';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const COMPRESS_QUALITY = 0.85;
const MAX_DIMENSION = 2400; // Max width or height in pixels
const UPLOAD_CONCURRENCY = 3;

const compressImage = (file) => {
    return new Promise((resolve) => {
        // Skip non-image files
        if (!file.type.startsWith('image/')) {
            resolve(file);
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let { width, height } = img;

            // Scale down if larger than max dimension
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob || blob.size >= file.size) {
                        // Compression didn't help, use original
                        resolve(file);
                        return;
                    }
                    const compressed = new File([blob], file.name, {
                        type: 'image/jpeg',
                        lastModified: file.lastModified
                    });
                    resolve(compressed);
                },
                'image/jpeg',
                COMPRESS_QUALITY
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file); // Fall back to original on error
        };

        img.src = url;
    });
};

const uploadSingleFile = async (projectId, file) => {
        const compressed = await compressImage(file);
        const contentType = compressed.type || 'image/jpeg';

        const { data: urlData } = await documentAPI.getUploadUrl({
            projectId,
            fileName: file.name,
            contentType: contentType,
            documentType: 0 // Photo
        });

        //Straight to S3 on a presigned URL, so the file never passes through our API
        await axios.put(urlData.uploadUrl, compressed, {
            headers: { 'Content-Type' : contentType },
        });

        await documentAPI.saveMetadata({
            projectId,
            fileName: file.name,
            fileKey: urlData.fileKey,
            //The compressed size, not the original. this is what is actually stored
            fileSize: compressed.size,
            contentType,
            documentType: 0,
        });
};

// Uploads a batch and reports what happened.

// Never throws: a caller that has already saved a job must not be told the save failed 
// because the photo did

// Returns the failed FILES, not a count, so a a caller still holding them can offer a retry -
// which is the only thing between a failed upload and lost photo. 

export async function uploadPhotos(projectId, files, onProgress) {
        const skipped = [];

        const validFiles = files.filter((f) => {
            if (!f.type.startsWith('image/')) {
                skipped.push({ name: f.name, reason: 'not an image' });
                return false;
            }
            if (f.size > MAX_FILE_SIZE) {
                skipped.push({ name: f.name, reason: 'over 50MB' });
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) {
            return { total: 0, failed: [], skipped };
        }

        // A few at a time rather than striclty in series. Uploads go straight to S3, so each 
        // is a separate connection the phone can overlap - then photos was ten round trips in
        // a row on a link nowhere near saturated. 

        // Bounded rather than all at once: a full batch in parallel on a weak cell uplink makes
        // every photo slow instead of finishing any of them.

        // Workers pull from a shared cursor, so a slow photo holds up only its own worker instead
        // of a fixed slice of the batch. 

        const failed = [];
        let cursor = 0;
        let completed = 0;

        const worker = async () => {
            while (cursor < validFiles.length) {
                const file = validFiles[cursor++];
                try {
                    await uploadSingleFile(projectId, file);
                } catch (err) {
                    console.error(`Failed to upload ${file.name}:`, err);
                    failed.push(file);
                }
                // Counted per file, so progress reflects photos actually finished rather than
                // batches started. 
                completed += 1;
                onProgress?.(completed, validFiles.length);
            }
        };

        await Promise.all(
            Array.from({ length: Math.min(UPLOAD_CONCURRENCY, validFiles.length) }, worker)
        );

        return { total: validFiles.length, failed, skipped };
    }